// ─────────────────────────────────────────────────────────────────
// Ideation AV — Services seed data (exact copy supplied by client).
// This is rendered to the public site and is also intended to seed
// the Supabase `services` table in Phase 8 (admin panel).
// ─────────────────────────────────────────────────────────────────

export const U = (id: string, w = 1600, q = 85) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=${q}&auto=format&fit=crop`;

export interface ServiceDef {
  slug: string;
  name: string;
  icon: string;
  shortDesc: string;
  paragraph1: string;
  paragraph2: string;
  carousel: string[];
  featured: string;
  brands: string[];
}

export const SERVICES: ServiceDef[] = [
  {
    slug: "audio",
    name: "Audio Systems",
    icon: "🔊",
    shortDesc: "FOH, monitor, line array systems & microphones tuned for the room.",
    paragraph1:
      "From intimate boardroom presentations to massive outdoor festivals, our audio setups deliver pristine, powerful sound that reaches every corner of your venue. Our FOH engineers work with the world's leading consoles and speaker systems, designing custom delay and fill configurations, tuning every room to perfection.",
    paragraph2:
      "We handle full system design, rigging, cabling and operation. Every audio deployment is engineered from the ground up for the specific acoustic characteristics of your venue — ensuring your audience hears exactly what you intend, every time.",
    carousel: [
      U("1429962714451-bb934ecdc4ec", 1920, 88),
      U("1470229722913-7c0e2dbbafd3", 1920, 88),
      U("1493225457124-a3eb161ffa5f", 1920, 88),
    ],
    featured: U("1429962714451-bb934ecdc4ec", 2400, 90),
    brands: ["d&b audiotechnik", "JBL Professional", "Shure", "Sennheiser", "DiGiCo", "Yamaha Pro"],
  },
  {
    slug: "lighting",
    name: "Lighting Solutions",
    icon: "💡",
    shortDesc: "Moving heads, beams, washes & programmers painting the stage.",
    paragraph1:
      "Our lighting team combines technical precision with creative vision, crafting atmospheres that move audiences and complement every moment of your event. We design, programme and operate full lighting rigs — including moving heads, LED washes, beam fixtures, gobos, followspots and automated control systems.",
    paragraph2:
      "From subtle corporate elegance to dramatic concert productions, our lighting tells your story. Every show is programmed with meticulous attention to detail and operated by experienced lighting directors who understand how light shapes emotion.",
    carousel: [
      U("1505373877841-8d25f7d46678", 1920, 88),
      U("1493225457124-a3eb161ffa5f", 1920, 88),
      U("1465847899084-d164df4dedc6", 1920, 88),
    ],
    featured: U("1505373877841-8d25f7d46678", 2400, 90),
    brands: ["GrandMA", "Robe Lighting", "Clay Paky", "Chauvet", "Martin by Harman"],
  },
  {
    slug: "led-video",
    name: "LED Screens",
    icon: "📺",
    shortDesc: "Indoor & outdoor LED walls, video switching & live IMAG.",
    paragraph1:
      "From compact indoor stage backdrops to massive outdoor LED walls spanning entire building facades, we provide end-to-end LED screen services tailored to your event. Our team handles structural design, rigging, pixel mapping, video processing and real-time content playback.",
    paragraph2:
      "We work with the finest indoor and outdoor LED panels, ensuring flawless image quality, colour accuracy and brightness whatever the ambient light conditions. Every LED deployment is engineered for seamless integration with your content and creative vision.",
    carousel: [
      U("1492684223066-81342ee5ff30", 1920, 88),
      U("1505373877841-8d25f7d46678", 1920, 88),
      U("1571115764595-644a1f56a55c", 1920, 88),
    ],
    featured: U("1492684223066-81342ee5ff30", 2400, 90),
    brands: ["ROE Visual", "Novastar", "Brompton Technology", "Absen"],
  },
  {
    slug: "projection-mapping",
    name: "Projection Mapping",
    icon: "🎬",
    shortDesc: "Pixel-perfect content mapped to surfaces and architecture.",
    paragraph1:
      "We bring buildings, stages, products and unconventional surfaces to life with breathtaking projected visuals that blur the line between reality and imagination. Our projection mapping team handles full creative concept development, 3D surface scanning, content creation, projector alignment and show operation.",
    paragraph2:
      "Whether it is a single product reveal or a multi-surface architectural spectacle, we deliver pixel-perfect precision. Every mapping project begins with detailed technical planning and ends with a moment that leaves audiences speechless.",
    carousel: [
      U("1514525253161-7a46d19cd819", 1920, 88),
      U("1492684223066-81342ee5ff30", 1920, 88),
      U("1493225457124-a3eb161ffa5f", 1920, 88),
    ],
    featured: U("1514525253161-7a46d19cd819", 2400, 90),
    brands: ["Barco", "Christie Digital", "Dataton Watchout", "Resolume"],
  },
  {
    slug: "sfx-laser",
    name: "SFX & Laser",
    icon: "✨",
    shortDesc: "Cryo, confetti, CO2, low-fog, sparks & ILDA-class lasers.",
    paragraph1:
      "From dramatic CO2 jet blasts and precision laser shows to choreographed confetti cannons and atmospheric haze, our SFX team engineers the moments audiences talk about for years. Every effect is designed with safety as the top priority.",
    paragraph2:
      "Our certified SFX operators follow strict protocols and coordinate closely with venue management and local authorities. We handle full risk assessments, permits and safe operation of all pyrotechnic and laser systems — delivering spectacular results with zero compromise on safety.",
    carousel: [
      U("1465847899084-d164df4dedc6", 1920, 88),
      U("1429962714451-bb934ecdc4ec", 1920, 88),
      U("1571115764595-644a1f56a55c", 1920, 88),
    ],
    featured: U("1465847899084-d164df4dedc6", 2400, 90),
    brands: ["Pangolin Laser", "Magic FX", "Le Maitre"],
  },
  {
    slug: "rigging-staging",
    name: "Rigging & Staging",
    icon: "⚙️",
    shortDesc: "Certified truss, ground support, motors, custom stage builds & scenic.",
    paragraph1:
      "Our certified rigging team designs and installs custom truss structures that safely support lighting, audio, LED and scenic elements at any height, any scale. From ground-support systems and delay towers to complex multi-point motor-hoist configurations, every rigging design is backed by engineering calculations.",
    paragraph2:
      "On the staging side we build everything from simple performance platforms to complex multi-level architectural sets — handling structural design, material procurement, fabrication, site build and strike. Every rigging and stage design is signed off by certified professionals and engineered for safety, speed of build and visual impact.",
    carousel: [
      U("1514525253161-7a46d19cd819", 1920, 88),
      U("1492684223066-81342ee5ff30", 1920, 88),
      U("1518609878373-06d740f60d8b", 1920, 88),
      U("1519167758481-83f550bb49b3", 1920, 88),
    ],
    featured: U("1514525253161-7a46d19cd819", 2400, 90),
    brands: ["Prolyte", "Chainmaster", "Liftket", "Layher", "Steeldeck", "Tomcat"],
  },
  {
    slug: "technical-drawings",
    name: "Technical Drawings",
    icon: "📐",
    shortDesc: "CAD plots, fixture plans, signal flow & venue submissions.",
    paragraph1:
      "Our technical drawings service covers everything from initial concept layouts to fully detailed construction and rigging documentation — giving your entire team a single source of truth. We produce stage plots, rigging plans, electrical schematics, speaker placement drawings, structural layouts and venue overlays.",
    paragraph2:
      "Every drawing is produced to industry standards and can be tailored for venue submission, client approval or contractor briefing. Clear, accurate technical documentation eliminates on-site surprises and ensures every element of your production is planned to perfection before a single case is unloaded.",
    carousel: [
      U("1518609878373-06d740f60d8b", 1920, 88),
      U("1492684223066-81342ee5ff30", 1920, 88),
      U("1511578314322-379afb476865", 1920, 88),
    ],
    featured: U("1518609878373-06d740f60d8b", 2400, 90),
    brands: ["AutoCAD", "Vectorworks", "WYSIWYG"],
  },
  {
    slug: "3d-visualisation",
    name: "3D Visualization",
    icon: "🎨",
    shortDesc: "Photo-real pre-visualisation before a single cable is run.",
    paragraph1:
      "Our visualisation service lets you and your clients see, feel and approve the full production design in stunning detail — eliminating surprises and aligning every stakeholder before build day. We produce full 3D models of stages, rigging, lighting rigs, LED setups and scenic elements, rendered in real lighting conditions with accurate camera angles.",
    paragraph2:
      "Ideal for client presentations, sponsor approvals and internal production reviews. See your vision come to life before the first flight case arrives on site.",
    carousel: [
      U("1574691250077-03a929faece5", 1920, 88),
      U("1518609878373-06d740f60d8b", 1920, 88),
      U("1505373877841-8d25f7d46678", 1920, 88),
    ],
    featured: U("1574691250077-03a929faece5", 2400, 90),
    brands: ["Cinema 4D", "Capture", "Blender", "WYSIWYG"],
  },
];

export const getServiceBySlug = (slug?: string) => SERVICES.find((s) => s.slug === slug);
